import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { Mail, Lock, User, Loader2, ArrowRight, KeyRound } from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';

export function Login() {
  const [mode, setMode] = useState<'login' | 'register' | 'verify' | 'forgot_password' | 'reset_password'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const {
    login,
    loginWithGoogle,
    register,
    confirmAccount,
    resendCode,
    forgotPassword,
    confirmForgotPassword,
    isLoading,
    error,
    clearError,
    pendingConfirmEmail
  } = useAuthStore();
  const navigate = useNavigate();

  // Switch to verify mode if there's a pending confirmation
  useEffect(() => {
    if (pendingConfirmEmail) {
      setEmail(pendingConfirmEmail);
      setMode('verify');
    }
  }, [pendingConfirmEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      if (mode === 'login') {
        await login(email, password);
        navigate('/');
      } else if (mode === 'register') {
        await register(email, password, name);
        // Mode will automatically switch to 'verify' via useEffect
      } else if (mode === 'verify') {
        await confirmAccount(email, verificationCode);
        setMode('login');
        setVerificationCode('');
      } else if (mode === 'forgot_password') {
        await forgotPassword(email);
        setMode('reset_password');
      } else if (mode === 'reset_password') {
        await confirmForgotPassword(email, verificationCode, password);
        setMode('login');
        setVerificationCode('');
        setPassword('');
        toast.success('Password reset successfully. Please sign in with your new password.');
      }
    } catch {
      // Error is handled by the store
    }
  };

  const handleResendCode = async () => {
    clearError();
    try {
      await resendCode(email);
    } catch {
      // Error is handled by the store
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      {/* Background gradient */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-1/2 left-1/2 h-[1000px] w-[1000px] -translate-x-1/2 rounded-full bg-gradient-brand opacity-25 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-surface shadow-glow">
            <img src="/personal-chotu-logo-base.png" alt="Chotu" className="h-12 w-12" />
          </div>
          <h1 className="text-2xl font-bold text-gradient">Chotu Assistant</h1>
          <p className="mt-2 text-text-secondary">Your AI-powered productivity companion</p>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-border bg-surface p-8">
          {/* Tab switcher - hidden in verify and password reset modes */}
          {mode !== 'verify' && mode !== 'forgot_password' && mode !== 'reset_password' && (
            <div className="mb-6 flex rounded-lg border border-border p-1">
              <button
                onClick={() => setMode('login')}
                className={clsx(
                  'flex-1 rounded-md py-2 text-sm font-medium transition-colors',
                  mode === 'login'
                    ? 'bg-surface-hover text-text-primary'
                    : 'text-text-tertiary hover:text-text-secondary'
                )}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('register')}
                className={clsx(
                  'flex-1 rounded-md py-2 text-sm font-medium transition-colors',
                  mode === 'register'
                    ? 'bg-surface-hover text-text-primary'
                    : 'text-text-tertiary hover:text-text-secondary'
                )}
              >
                Sign Up
              </button>
            </div>
          )}

          {/* Verification mode header */}
          {mode === 'verify' && (
            <div className="mb-6 text-center">
              <h2 className="text-lg font-semibold">Verify your email</h2>
              <p className="mt-1 text-sm text-text-secondary">
                We sent a code to <span className="text-text-primary">{email}</span>
              </p>
            </div>
          )}

          {/* Forgot Password header */}
          {mode === 'forgot_password' && (
            <div className="mb-6 text-center">
              <h2 className="text-lg font-semibold">Forgot Password</h2>
              <p className="mt-1 text-sm text-text-secondary">
                Enter your email to receive a reset code
              </p>
            </div>
          )}

          {/* Reset Password header */}
          {mode === 'reset_password' && (
            <div className="mb-6 text-center">
              <h2 className="text-lg font-semibold">Reset Password</h2>
              <p className="mt-1 text-sm text-text-secondary">
                Enter the code sent to <span className="text-text-primary">{email}</span> and your new password
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="mb-1 block text-sm text-text-secondary">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input pl-10"
                    placeholder="Your name"
                  />
                </div>
              </div>
            )}

            {mode !== 'verify' && mode !== 'reset_password' && (
              <div>
                <label className="mb-1 block text-sm text-text-secondary">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-10"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>
            )}

            {(mode === 'login' || mode === 'register' || mode === 'reset_password') && (
              <div>
                <label className="mb-1 block text-sm text-text-secondary">
                  {mode === 'reset_password' ? 'New Password' : 'Password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10"
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                </div>
              </div>
            )}

            {(mode === 'verify' || mode === 'reset_password') && (
              <div>
                <label className="mb-1 block text-sm text-text-secondary">
                  {mode === 'reset_password' ? 'Reset Code' : 'Verification Code'}
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="input pl-10 text-center tracking-widest"
                    placeholder="123456"
                    required
                    maxLength={6}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-error-muted p-3 text-sm text-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-gradient w-full justify-center py-3"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {mode === 'login' && 'Sign In'}
                  {mode === 'register' && 'Create Account'}
                  {mode === 'verify' && 'Verify'}
                  {mode === 'forgot_password' && 'Send Reset Code'}
                  {mode === 'reset_password' && 'Reset Password'}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          {/* Social login - only shown on login/register modes */}
          {mode !== 'verify' && mode !== 'forgot_password' && mode !== 'reset_password' && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-surface px-2 text-text-tertiary">Or continue with</span>
                </div>
              </div>

              <button
                type="button"
                onClick={loginWithGoogle}
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-surface py-3 text-sm font-medium text-text-primary transition-colors hover:bg-surface-hover disabled:opacity-50"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
            </>
          )}

          {mode === 'login' && (
            <p className="mt-4 text-center text-sm text-text-tertiary">
              <button
                type="button"
                onClick={() => {
                  setMode('forgot_password');
                  clearError();
                }}
                className="text-accent hover:underline"
              >
                Forgot password?
              </button>
            </p>
          )}

          {(mode === 'forgot_password' || mode === 'reset_password') && (
            <p className="mt-4 text-center text-sm text-text-tertiary">
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  clearError();
                }}
                className="text-text-tertiary hover:text-text-secondary"
              >
                ← Back to login
              </button>
            </p>
          )}

          {mode === 'verify' && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                onClick={() => setMode('login')}
                className="text-text-tertiary hover:text-text-secondary"
              >
                ← Back to login
              </button>
              <button
                onClick={handleResendCode}
                disabled={isLoading}
                className="text-accent hover:underline"
              >
                Resend code
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-text-tertiary">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}

